import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useMemberUna } from "@/hooks/useMemberUna";
import { useAuth } from "@/contexts/AuthContext";
import { useJourneyStatus } from "@/hooks/useJourneyStatus";
import {
  getActionForRule,
  scoreStatusLine,
} from "@/lib/governanceHealth";

const PERIOD_YEAR = 2026;

export const GovernanceHealthWidget = () => {
  const { user } = useAuth();
  const { unaId, isLoading: unaLoading } = useMemberUna();
  const { journeyCompleted } = useJourneyStatus(user?.id);

  const { data: score, isLoading: scoreLoading } = useQuery({
    queryKey: ["gh-score", unaId, PERIOD_YEAR],
    queryFn: async () => {
      const { data } = await supabase
        .from("una_governance_scores")
        .select("score, satisfied_count, applicable_count")
        .eq("una_id", unaId!)
        .eq("period_year", PERIOD_YEAR)
        .maybeSingle();
      return data;
    },
    enabled: !!unaId,
  });

  const { data: nextItems = [] } = useQuery({
    queryKey: ["gh-next-items", unaId],
    queryFn: async () => {
      const { data } = await supabase
        .from("una_compliance_items")
        .select("id, rule_key, status, due_date, rule:compliance_rule_catalog!inner(title, weight, rule_group, evidence_types)")
        .eq("una_id", unaId!)
        .in("status", ["overdue", "due_soon", "upcoming"])
        .limit(20);
      const rows = (data || []) as any[];
      const statusRank: Record<string, number> = { overdue: 0, due_soon: 1, upcoming: 2 };
      rows.sort((a, b) => {
        const sa = statusRank[a.status] ?? 3;
        const sb = statusRank[b.status] ?? 3;
        if (sa !== sb) return sa - sb;
        return (b.rule?.weight ?? 0) - (a.rule?.weight ?? 0);
      });
      return rows.slice(0, 3);
    },
    enabled: !!unaId,
  });

  if (unaLoading || (unaId && scoreLoading)) {
    return null;
  }

  const scoreValue = score?.score ?? null;

  // Build the action list. When the journey isn't complete, force the first
  // action to "Continue My Journey"; keep other suggested items as secondary.
  const suggestions = nextItems.map((item: any) => ({
    id: item.id,
    title: item.rule?.title ?? "Governance action",
    ...getActionForRule(
      item.rule_key,
      item.rule?.rule_group ?? "",
      item.id,
      item.rule?.evidence_types,
    ),
    primary: false,
  }));

  const actions = journeyCompleted
    ? suggestions
    : [
        {
          id: "journey",
          title: "Complete your onboarding journey",
          href: "/journey",
          label: "Continue My Journey",
          primary: true,
        },
        ...suggestions.filter((s) => s.href !== "/journey"),
      ].slice(0, 3);

  return (
    <Card className="border-gold/30">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-gold" />
          <CardTitle className="text-2xl">Governance Health</CardTitle>
        </div>
        <CardDescription>
          {scoreStatusLine(scoreValue)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {!unaId || scoreValue === null ? (
          <div className="rounded-lg border border-dashed p-4 flex items-start gap-3">
            <Heart className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              Your governance record will appear here once your UNA is set up.
              Every small step counts.
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-6">
            <div className="flex flex-col items-center justify-center rounded-full border-4 border-gold/40 w-24 h-24">
              <span className="text-4xl font-semibold text-charcoal leading-none">
                {scoreValue}
              </span>
              <span className="text-xs text-muted-foreground mt-1">of 100</span>
            </div>
            <div className="text-sm text-muted-foreground">
              <div className="font-medium text-foreground">
                {score?.satisfied_count ?? 0} of {score?.applicable_count ?? 0} satisfied
              </div>
              <div className="mt-1">2026 record</div>
            </div>
          </div>
        )}

        {actions.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Next actions</div>
            <div className="space-y-2">
              {actions.map((a, idx) => (
                <Link
                  key={a.id}
                  to={a.href}
                  className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 hover:bg-muted/50 transition ${
                    a.primary
                      ? "border-gold/50 bg-gold/10"
                      : "border-border"
                  }`}
                >
                  <span className={`text-sm ${a.primary ? "font-medium" : ""}`}>
                    {idx + 1}. {a.title}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    {a.label}
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Button asChild variant="outline" className="w-full">
          <Link to="/governance/health">View full governance health</Link>
        </Button>
      </CardContent>
    </Card>
  );
};
